import images from '@assets/images/index'
import { useAssessmentDropdownFilters } from '@hooks/useAssessmentDropdownFilters'
import { useQuery } from '@tanstack/react-query'
import { GetAPIparams, useDropdownMenu } from '@utils/assessmentReportUtils'
import { Breadcrumb, Flex } from 'antd'
import { useMemo, useState } from 'react'

import { Link, useLocation, useNavigate } from '@suntex.core/nav'
import {
  Database_Procs_GetGoalsIndexPlayers_Item,
  Database_Procs_GetGoalsIndexTeams_Item
} from '@suntex.fim/api-client'
import {
  AssessmentColumnType as ColumnType,
  goalsIndexPhaseOptions,
  useAssessmentTable
} from '@suntex.fim/assessment'
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react'
import {
  $GoalsIndexPhase,
  EntityLevel,
  GoalsIndexPhase
} from '@suntex.fim/enums'
import { useFramework } from '@suntex.fim/framework'
import { SendMessage } from '@suntex.fim/messages'
import { useAppQueryString } from '@suntex.fim/nav'
import { useOptionalTeamInfo } from '@suntex.fim/team'
import {
  Button,
  Dropdown,
  LevelSelect,
  LoaderOverlay,
  SearchInput
} from '@suntex.fim/ui'

import {
  GoalIndexReportData,
  columns,
  hidableSelectLabel,
  teamColumns,
  teamHidableSelectLabel
} from './AssessmentReport/AssessmentReportGoalIndexByPlayerTable'
import AssessmentReportNavigation from './AssessmentReport/AssessmentReportNavigation'
import AssessmentReportSummaryDashboard from './AssessmentReport/AssessmentReportSummaryDashboard'
import SelectedRowPopUp from './AssessmentReport/SelectedRowPopUp'

function AssessmentReportGoalIndexByPlayer() {
  const { api } = useFramework()
  const { phase: queryPhase } = useAppQueryString()
  const team = useOptionalTeamInfo()
  const { login } = useAuth(isAuthenticated)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const [searchQuery, setSearchQuery] = useState('')
  const [sendMessageData, setSendMessageData] = useState<any[]>([])
  const [isExporting, setIsExporting] = useState(false)
  const isSchool =
    login.entityLevel === EntityLevel.School &&
    location.pathname.includes('school')
  const memoColumns = useMemo<ColumnType<GoalIndexReportData>[]>(
    () => (isSchool ? teamColumns() : columns()),
    []
  )
  const [dropdownFilters, setDropdownFilters] = useAssessmentDropdownFilters()
  const { id, year, grade, sortingkey } = GetAPIparams(
    isSchool,
    dropdownFilters
  )
  const [phase, setPhase] = useState<GoalsIndexPhase>(
    $GoalsIndexPhase.fromOptional(queryPhase) ?? GoalsIndexPhase.Early
  )

  const { data: queryData, isLoading } = useQuery({
    queryKey: [
      'assessment',
      'goalsIndexByPlayer',
      id,
      isSchool,
      phase,
      year,
      grade
    ],
    queryFn: async () => {
      if (isSchool) {
        const response = await api.get_Assessment_GoalsByTeam({
          id: id!,
          level: login.entityLevel,
          phase,
          year: year!,
          grade
        })
        return { type: 'school' as const, data: response.goalsIndexTeams || [] }
      } else {
        const response = await api.get_Assessment_GoalsIndexByPlayer({
          entityId: id!,
          phase
        })
        return {
          type: 'player' as const,
          data: response.goalsIndexByPlayers || []
        }
      }
    },
    enabled: id != null
  })

  const goalIndexByPlayer = queryData?.type === 'player' ? queryData.data : []
  const goalIndexByTeam = queryData?.type === 'school' ? queryData.data : []
  const { table, Table } = useAssessmentTable<
    | Database_Procs_GetGoalsIndexPlayers_Item
    | Database_Procs_GetGoalsIndexTeams_Item
  >({
    data: isSchool ? goalIndexByTeam : goalIndexByPlayer,
    columns: memoColumns,
    isLoading,
    globalFilter: searchQuery,
    initialSorting: [{ id: sortingkey, desc: false }],
    hideFirstTwoColumns: false
  })

  const fileName = isSchool
    ? `${login.schoolName}_GoalsIndexByTeam`
    : `${team?.name}_GoalsIndexByPlayer`

  const exportColumn = isSchool ? teamHidableSelectLabel : hidableSelectLabel
  const items = useDropdownMenu({
    table,
    hidableSelectLabel: exportColumn,
    fileName,
    hasColumnChange: isSchool ? ['teamGrade'] : [],
    isSchool,
    setIsLoading: setIsExporting
  })

  const selectedRow = table.getSelectedRowModel().flatRows
  const isDisabled = !selectedRow.length
  const sendMessageUsers = sendMessageData.length
    ? sendMessageData
    : selectedRow
  const selectedUserOptions = sendMessageUsers.map(
    ({ original }: { original: any }) => ({
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      label: original.playerName || original.playerId,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      value: original.playerId
    })
  )

  // update URL params
  const updateURLParams = (key: string | undefined, value: string) => {
    if (key && value) {
      setPhase($GoalsIndexPhase.from(Number(value)))
      // Create new URLSearchParams from current location
      const searchParams = new URLSearchParams(location.search)
      // Set the new parameter value
      searchParams.set(key, value)

      // Create the new search string
      const newSearchString = searchParams.toString()

      // Use navigate to update the URL
      navigate({
        pathname: location.pathname,
        search: `?${newSearchString}`
      })
    }
  }

  return (
    <>
      {/* navigation */}
      <Flex vertical gap="20px" style={{ position: 'relative' }}>
        <LoaderOverlay isLoading={isLoading || isExporting} />
        <Breadcrumb
          className="print-hidden"
          items={[
            {
              title: 'Team Assessment Reports-Goals Index'
            },
            {
              title: (
                <Link
                  to={
                    isSchool
                      ? `/school/${id}/assessment-report/goals-index`
                      : `/team/${id}/assessment-report/goals-index`
                  }
                >
                  <b>Goals Index By {isSchool ? 'School' : 'Team'}</b>
                </Link>
              )
            }
          ]}
        />
        <AssessmentReportNavigation
          exportButton={
            <Flex className="export-btn print-hidden">
              <Button
                style={{ marginRight: '10px' }}
                shape="round"
                intent="secondary"
                onClick={() => {
                  if (isSchool) {
                    navigate(`/school/${id}/assessment-report/goals-index`)
                  } else {
                    navigate(`/team/${id}/assessment-report/goals-index`)
                  }
                }}
              >
                View Report by {isSchool ? 'School' : 'Team'}
              </Button>
              <Dropdown
                label="Export"
                menu={{ items }}
                icon={<images.ArrowSquareDown />}
                overlayClassName="export-dropdown"
              />
            </Flex>
          }
          title={`Goals Index By ${isSchool ? 'Team' : 'Player'}`}
          displayReport={false}
          dropdownFilters={dropdownFilters}
          setDropDownFilters={setDropdownFilters}
          isSchool={isSchool}
        />
        <AssessmentReportSummaryDashboard
          dropdownFilters={dropdownFilters}
          isSchool={isSchool}
        />
        {/* result */}
        <div className="main-result">
          <Flex
            justify="space-between"
            align="center"
            className="main-result__toolbar"
            wrap
          >
            <h4 className="title">
              {isSchool ? 'School Teams' : 'Team Players'}
            </h4>

            <Flex gap="10px" className="goal-index-toolbar" wrap>
              <div className="report-filter">
                <p className="dd-label fw-bold text-sm">Phase:</p>
                <LevelSelect
                  value={phase}
                  onChange={(newLevel) =>
                    updateURLParams('phase', newLevel.toString())
                  }
                  options={goalsIndexPhaseOptions}
                />
              </div>
              <div className="report-search">
                <SearchInput value={searchQuery} onChange={setSearchQuery} />
              </div>
              {login.entityLevel !== EntityLevel.School && (
                <Button
                  intent="tertiary"
                  size="small"
                  disabled={isDisabled}
                  onClick={() => setIsModalOpen(true)}
                  className="print-hidden"
                >
                  SEND MESSAGE
                </Button>
              )}
            </Flex>
          </Flex>
          <Table />
        </div>
        {!isModalOpen && (
          <SelectedRowPopUp
            selectedRow={selectedRow}
            resetRowSelection={table.resetRowSelection}
            handleSendMessage={() => setIsModalOpen(true)}
          />
        )}
        <SendMessage
          isModalOpen={isModalOpen}
          closeModal={(isSuccess) => {
            setIsModalOpen(false)
            setSendMessageData([])
            if (isSuccess) {
              table.resetRowSelection()
            }
          }}
          selectedUserOptions={selectedUserOptions}
          recipientEntityId={team?.id}
        />
      </Flex>
    </>
  )
}

export default AssessmentReportGoalIndexByPlayer
